import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { cn } from "@nexirt/ui";

/**
 * PricingPanel — Dynamic Output mock for "What does a portal project
 * cost end-to-end?" (Rev 25 Phase 2 Loop I). Three tier cards with the
 * design's "From $X" wording (owner default §6).
 */

type Tier = {
  name: string;
  tag: string;
  price: string;
  priceSuffix?: string;
  desc: string;
  features: string[];
  cta: string;
  ctaTo: string;
  featured?: boolean;
};

const TIERS: Tier[] = [
  {
    name: "Marketing site",
    tag: "Starter",
    price: "From $12K",
    desc: "A premium marketing site that loads under a second and reads like the brand it represents.",
    features: ["6–8 pages, fully responsive", "CMS-ready content blocks", "CloudFront + edge cache"],
    cta: "Estimate scope",
    ctaTo: "/contact",
  },
  {
    name: "Portal with auth",
    tag: "Growth · most picked",
    price: "From $45K",
    desc: "Multi-tenant client or admin portal with Cognito auth, billing, audit log and a clean React SPA.",
    features: ["Cognito + role-based access", "Stripe Billing wired", "Audit log + admin tooling"],
    cta: "Start a project",
    ctaTo: "/contact",
    featured: true,
  },
  {
    name: "Bespoke platform",
    tag: "Enterprise",
    price: "Custom",
    desc: "Mapping, payroll, geospatial — line-of-business systems with their own data models and ops surface.",
    features: ["Architecture + ADRs", "Three.js / d3-geo scenes", "Care plan included"],
    cta: "Talk to our team",
    ctaTo: "/contact",
  },
];

export function PricingPanel() {
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      {TIERS.map((t) => (
        <div
          key={t.name}
          className={cn(
            "flex flex-col rounded-nx2 border bg-nx-surface/60 p-4",
            t.featured
              ? "border-nx-accent-2/40 shadow-[0_0_30px_-15px_var(--nx-accent-2-glow)]"
              : "border-nx-line",
          )}
        >
          <div
            className={cn(
              "inline-flex w-fit font-mono text-[10px] uppercase tracking-[0.14em]",
              t.featured ? "text-nx-accent-2" : "text-nx-text-faint",
            )}
          >
            {t.tag}
          </div>
          <h4 className="mt-1.5 font-display text-base font-semibold text-nx-text">
            {t.name}
          </h4>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="font-display text-2xl font-semibold tracking-tight text-nx-text">
              {t.price}
            </span>
            {t.priceSuffix ? (
              <span className="text-[11px] text-nx-text-faint">{t.priceSuffix}</span>
            ) : null}
          </div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-nx-text-dim">
            {t.desc}
          </p>
          <ul className="mt-3 space-y-1.5">
            {t.features.map((f) => (
              <li
                key={f}
                className="flex items-start gap-2 text-[12.5px] text-nx-text-dim"
              >
                <Check
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-nx-accent-2"
                  strokeWidth={2.4}
                  aria-hidden
                />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Link
            to={t.ctaTo}
            className={cn(
              "mt-auto inline-flex items-center justify-center gap-1.5 rounded-nx2 px-3 py-2 text-[12.5px] font-medium transition-[transform,filter] duration-150 ease-nx",
              t.featured
                ? "bg-nx-accent text-white shadow-[var(--nx-shadow-glow)] hover:-translate-y-[1px] hover:brightness-110"
                : "border border-nx-line-strong text-nx-text hover:bg-nx-surface-2",
              "mt-4",
            )}
          >
            {t.cta} →
          </Link>
        </div>
      ))}
    </div>
  );
}

export default PricingPanel;
